/**
 * Tooling/package selection — when the plan has multiple candidates for a step,
 * rank them by explicit signals and pick the winner BEFORE building:
 *
 *   score = w_pop · popularity + w_maint · maintenance + w_docs · documentation
 *
 * Popularity comes from npm weekly downloads, maintenance from days since the
 * last publish, documentation from a known docs site. Signals are fetched from
 * the npm registry when the network allows and fall back to a curated baseline
 * of mainstream packages — biasing selection toward widely-used, maintained,
 * well-documented options by default. Every choice records WHY (the deciding
 * signals), so the selection is auditable from the plan checkpoint.
 */
import type { PlanningConfig } from './planning-config.js';

export interface CandidateSignals {
  readonly weeklyDownloads?: number;
  readonly lastPublishDays?: number;
  readonly hasOfficialDocs?: boolean;
  readonly docsUrl?: string;
  /** Where the signals came from. */
  readonly source: 'npm-registry' | 'baseline' | 'none';
}

export interface RankedCandidate {
  readonly name: string;
  /** 0..1 composite of the weighted signals. */
  readonly score: number;
  readonly signals: CandidateSignals;
}

export interface SelectionResult {
  /** The decision area (e.g. "http server", "validation"). */
  readonly area: string;
  readonly chosen: string;
  /** The deciding signals, spelled out. */
  readonly rationale: string;
  /** Full ranking, best first (inspectable). */
  readonly candidates: readonly RankedCandidate[];
}

export type SignalFetcher = (name: string, timeoutMs: number) => Promise<CandidateSignals | null>;

/**
 * Curated mainstream baseline (approximate, refreshed manually): keeps ranking
 * deterministic offline and anchors the "prefer widely-used" default. Live npm
 * signals override these when reachable.
 */
export const BASELINE_SIGNALS: Readonly<Record<string, Omit<CandidateSignals, 'source'>>> = {
  react: { weeklyDownloads: 28_000_000, lastPublishDays: 30, hasOfficialDocs: true, docsUrl: 'https://react.dev' },
  next: { weeklyDownloads: 7_000_000, lastPublishDays: 7, hasOfficialDocs: true, docsUrl: 'https://nextjs.org/docs' },
  vue: { weeklyDownloads: 5_500_000, lastPublishDays: 30, hasOfficialDocs: true, docsUrl: 'https://vuejs.org/guide/' },
  svelte: { weeklyDownloads: 2_200_000, lastPublishDays: 14, hasOfficialDocs: true, docsUrl: 'https://svelte.dev/docs' },
  express: { weeklyDownloads: 35_000_000, lastPublishDays: 90, hasOfficialDocs: true, docsUrl: 'https://expressjs.com' },
  fastify: { weeklyDownloads: 2_500_000, lastPublishDays: 14, hasOfficialDocs: true, docsUrl: 'https://fastify.dev/docs/latest/' },
  '@nestjs/core': { weeklyDownloads: 4_000_000, lastPublishDays: 14, hasOfficialDocs: true, docsUrl: 'https://docs.nestjs.com' },
  koa: { weeklyDownloads: 1_800_000, lastPublishDays: 365, hasOfficialDocs: true, docsUrl: 'https://koajs.com' },
  prisma: { weeklyDownloads: 3_500_000, lastPublishDays: 14, hasOfficialDocs: true, docsUrl: 'https://www.prisma.io/docs' },
  'drizzle-orm': { weeklyDownloads: 1_500_000, lastPublishDays: 7, hasOfficialDocs: true, docsUrl: 'https://orm.drizzle.team' },
  typeorm: { weeklyDownloads: 2_000_000, lastPublishDays: 60, hasOfficialDocs: true, docsUrl: 'https://typeorm.io' },
  zod: { weeklyDownloads: 18_000_000, lastPublishDays: 14, hasOfficialDocs: true, docsUrl: 'https://zod.dev' },
  vitest: { weeklyDownloads: 9_000_000, lastPublishDays: 7, hasOfficialDocs: true, docsUrl: 'https://vitest.dev' },
  jest: { weeklyDownloads: 24_000_000, lastPublishDays: 120, hasOfficialDocs: true, docsUrl: 'https://jestjs.io/docs/getting-started' },
  playwright: { weeklyDownloads: 6_000_000, lastPublishDays: 14, hasOfficialDocs: true, docsUrl: 'https://playwright.dev/docs/intro' },
  typescript: { weeklyDownloads: 55_000_000, lastPublishDays: 30, hasOfficialDocs: true, docsUrl: 'https://www.typescriptlang.org/docs/' },
  axios: { weeklyDownloads: 50_000_000, lastPublishDays: 60, hasOfficialDocs: true, docsUrl: 'https://axios-http.com/docs/intro' },
  tailwindcss: { weeklyDownloads: 14_000_000, lastPublishDays: 7, hasOfficialDocs: true, docsUrl: 'https://tailwindcss.com/docs' },
};

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

/** Score one candidate's signals against the configured weights (0..1). */
export function scoreSignals(signals: CandidateSignals, cfg: PlanningConfig): number {
  const w = cfg.selection.weights;
  const totalWeight = w.popularity + w.maintenance + w.docs;
  if (totalWeight <= 0) return 0;

  // Popularity: log-scale weekly downloads; ~10M/wk ≈ 0.875, ~1k/wk ≈ 0.375.
  const downloads = signals.weeklyDownloads ?? 0;
  const popularity = downloads > 0 ? clamp01(Math.log10(downloads) / 8) : 0;

  // Maintenance: full credit within the freshness window, linear decay to 0 at 4× it.
  const days = signals.lastPublishDays;
  const window = cfg.selection.freshnessWindowDays;
  const maintenance =
    days === undefined ? 0 : days <= window ? 1 : clamp01(1 - (days - window) / (3 * window));

  const docs = signals.hasOfficialDocs ? 1 : 0;

  return (w.popularity * popularity + w.maintenance * maintenance + w.docs * docs) / totalWeight;
}

/** Live npm signals: registry metadata (last publish) + weekly downloads. */
export const fetchNpmSignals: SignalFetcher = async (name, timeoutMs) => {
  try {
    const enc = encodeURIComponent(name).replace('%40', '@');
    const [meta, dl] = await Promise.all([
      fetch(`https://registry.npmjs.org/${enc}`, { signal: AbortSignal.timeout(timeoutMs) }),
      fetch(`https://api.npmjs.org/downloads/point/last-week/${enc}`, {
        signal: AbortSignal.timeout(timeoutMs),
      }),
    ]);
    if (!meta.ok) return null;
    const metaJson = (await meta.json()) as {
      time?: Record<string, string>;
      homepage?: string;
    };
    const modified = metaJson.time?.modified;
    const lastPublishDays = modified
      ? Math.max(0, Math.round((Date.now() - Date.parse(modified)) / 86_400_000))
      : undefined;
    let weeklyDownloads: number | undefined;
    if (dl.ok) {
      const dlJson = (await dl.json()) as { downloads?: number };
      if (typeof dlJson.downloads === 'number') weeklyDownloads = dlJson.downloads;
    }
    return {
      ...(weeklyDownloads !== undefined ? { weeklyDownloads } : {}),
      ...(lastPublishDays !== undefined ? { lastPublishDays } : {}),
      hasOfficialDocs: typeof metaJson.homepage === 'string' && metaJson.homepage.length > 0,
      ...(typeof metaJson.homepage === 'string' && metaJson.homepage ? { docsUrl: metaJson.homepage } : {}),
      source: 'npm-registry' as const,
    };
  } catch {
    return null; // offline / blocked / timeout → caller falls back to the baseline
  }
};

function describe(c: RankedCandidate): string {
  const s = c.signals;
  const bits: string[] = [];
  if (s.weeklyDownloads !== undefined) bits.push(`~${Math.round(s.weeklyDownloads / 1_000_000 * 10) / 10}M weekly downloads`);
  if (s.lastPublishDays !== undefined) bits.push(`published ${s.lastPublishDays}d ago`);
  bits.push(s.hasOfficialDocs ? 'official docs' : 'no official docs found');
  bits.push(`signals: ${s.source}`);
  return bits.join(', ');
}

/**
 * Rank candidates for one decision area and pick the winner. Live signals when
 * reachable, curated baseline otherwise, "no signals" scoring as a last resort
 * (an unknown niche package naturally ranks below a documented mainstream one).
 */
export async function rankCandidates(
  area: string,
  names: readonly string[],
  cfg: PlanningConfig,
  fetcher: SignalFetcher = fetchNpmSignals,
): Promise<SelectionResult | null> {
  const unique = [...new Set(names.map((n) => n.trim()).filter(Boolean))];
  if (unique.length === 0) return null;

  const ranked: RankedCandidate[] = await Promise.all(
    unique.map(async (name) => {
      const live = await fetcher(name, cfg.selection.fetchTimeoutMs);
      const baseline = BASELINE_SIGNALS[name];
      // Live signals win field-by-field, but a PARTIAL live result (e.g. the
      // downloads endpoint failed) must not zero out a known mainstream
      // package — fall back to the baseline for the missing fields.
      let signals: CandidateSignals;
      if (live && baseline) {
        signals = {
          ...baseline,
          ...Object.fromEntries(Object.entries(live).filter(([, v]) => v !== undefined)),
          source: live.source,
        };
      } else {
        signals = live ?? (baseline ? { ...baseline, source: 'baseline' } : { source: 'none' });
      }
      return { name, score: scoreSignals(signals, cfg), signals };
    }),
  );
  ranked.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));

  const best = ranked[0];
  if (!best) return null;
  const runnerUp = ranked[1];
  const margin = runnerUp ? ` (next: ${runnerUp.name} ${runnerUp.score.toFixed(2)})` : '';
  return {
    area,
    chosen: best.name,
    rationale: `${best.name} ${best.score.toFixed(2)} — ${describe(best)}${margin}`,
    candidates: ranked,
  };
}
